import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { RiAddLine, RiSurveyLine } from '@remixicon/react'
import type { SurveyListItem, SurveyResponseMode, SurveyTemplate } from 'shared'
import { Alert, Badge, Button, Card, EmptyState, SectionSpinner, Select } from '../ui'
import PanelRow from './PanelRow'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'
import { SURVEY_STATUS_TONE } from '../../lib/status'
import { formatDate } from '../../lib/dates'

/**
 * Whether this company has answered, said as a state rather than as the
 * presence or absence of a badge.
 *
 * The row used to show "Answered" when the survey had been submitted and
 * nothing at all when it had not — which is the weakest possible signal for
 * the one thing an operator scans a survey list for. A published survey
 * nobody has answered is now a visible state, and so is a survey that closed
 * without one.
 */
function answerState(
  survey: SurveyListItem,
): { tone: 'success' | 'warning' | 'neutral'; key: string; values?: Record<string, number> } | null {
  // An individual survey has no single answer to be in or out: what an operator
  // scans for is how many of the people it was sent to have given theirs.
  if (survey.response_mode === 'individual') {
    if (survey.respondent_total === null || survey.status === 'draft') return null
    const values = {
      submitted: survey.respondent_submitted ?? 0,
      total: survey.respondent_total,
    }
    const complete = values.total > 0 && values.submitted >= values.total
    return {
      tone: complete ? 'success' : survey.status === 'published' ? 'warning' : 'neutral',
      key: 'surveys.respondentProgress',
      values,
    }
  }
  if (survey.submission_status === 'submitted') return { tone: 'success', key: 'surveys.answered' }
  if (survey.status === 'published') return { tone: 'warning', key: 'surveys.awaitingAnswer' }
  if (survey.status === 'closed') return { tone: 'neutral', key: 'surveys.unanswered' }
  return null
}

export default function WorkspaceSurveysPanel({ workspaceId }: { workspaceId: string }) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'

  const [surveys, setSurveys] = useState<SurveyListItem[]>([])
  const [templates, setTemplates] = useState<SurveyTemplate[]>([])
  const [templateId, setTemplateId] = useState('')
  const [responseMode, setResponseMode] = useState<SurveyResponseMode>('collective')
  const [anonymous, setAnonymous] = useState(false)
  const [allowsPublic, setAllowsPublic] = useState(false)
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const response = await api.get<{ data: SurveyListItem[] }>(
      `/api/workspaces/${workspaceId}/surveys`,
    )
    setSurveys(response.data)
  }, [workspaceId])

  useEffect(() => {
    Promise.all([
      load(),
      // Templates are an admin surface; an operator without access simply gets
      // the blank-survey path, which is not an error worth showing. Only
      // published ones are offered: a draft template is still being written.
      api
        .get<{ data: SurveyTemplate[] }>('/api/admin/survey-templates')
        .then((response) =>
          setTemplates(response.data.filter((template) => template.status === 'published')),
        )
        .catch(() => setTemplates([])),
    ])
      .catch((err) => setError(toErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [load])

  const create = async () => {
    setCreating(true)
    setError(null)

    try {
      const template = templates.find((entry) => entry.id === templateId)
      const response = await api.post<{ data: { id: string } }>(
        `/api/workspaces/${workspaceId}/surveys`,
        {
          title: template?.name ?? t('surveys.untitled'),
          template_id: templateId || undefined,
          response_mode: responseMode,
          anonymous: responseMode === 'individual' && anonymous,
          // Only an admin may allow a public link, and only they send the field.
          ...(isAdmin && responseMode === 'individual' && allowsPublic ? { allows_public: true } : {}),
        },
      )
      navigate(`/app/workspaces/${workspaceId}/surveys/${response.data.id}`)
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
      setCreating(false)
    }
  }

  if (loading) return <SectionSpinner />

  return (
    <div className="space-y-6">
      {error && <Alert>{error}</Alert>}

      <Card className="p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
          {t('surveys.newTitle')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('surveys.newDescription')}
        </p>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <div className="sm:max-w-xs sm:flex-1">
            <Select
              aria-label={t('surveys.template')}
              value={templateId}
              onChange={(event) => setTemplateId(event.target.value)}
              options={[
                { value: '', label: t('surveys.blank') },
                ...templates.map((template) => ({ value: template.id, label: template.name })),
              ]}
            />
          </div>
          <div className="sm:max-w-xs sm:flex-1">
            <Select
              aria-label={t('surveys.responseMode')}
              value={responseMode}
              onChange={(event) => {
                const next = event.target.value as SurveyResponseMode
                setResponseMode(next)
                if (next !== 'individual') {
                  setAnonymous(false)
                  setAllowsPublic(false)
                }
              }}
              options={[
                { value: 'collective', label: t('surveys.mode_collective') },
                { value: 'individual', label: t('surveys.mode_individual') },
              ]}
            />
          </div>
          <Button onClick={create} loading={creating}>
            <RiAddLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
            {t('surveys.create')}
          </Button>
        </div>

        {responseMode === 'individual' && (
          <label className="mt-4 flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={anonymous}
              onChange={(event) => setAnonymous(event.target.checked)}
              className="mt-0.5 size-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>
              <span className="block font-medium text-gray-900 dark:text-gray-50">
                {t('surveys.anonymousLabel')}
              </span>
              <span className="mt-0.5 block text-gray-500 dark:text-gray-400">
                {t('surveys.anonymousHint')}
              </span>
            </span>
          </label>
        )}

        {isAdmin && responseMode === 'individual' && (
          <label className="mt-4 flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={allowsPublic}
              onChange={(event) => setAllowsPublic(event.target.checked)}
              className="mt-0.5 size-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>
              <span className="block font-medium text-gray-900 dark:text-gray-50">
                {t('surveys.allowsPublicLabel')}
              </span>
              <span className="mt-0.5 block text-gray-500 dark:text-gray-400">
                {t('surveys.allowsPublicHint')}
              </span>
            </span>
          </label>
        )}
      </Card>

      {surveys.length === 0 ? (
        <Card>
          <EmptyState
            icon={<RiSurveyLine className="size-6" aria-hidden="true" />}
            title={t('surveys.emptyTitle')}
            description={t('surveys.emptyDescription')}
          />
        </Card>
      ) : (
        <Card className="divide-y divide-gray-100 dark:divide-gray-800">
          {surveys.map((survey) => {
            const answer = answerState(survey)

            return (
              <PanelRow
                key={survey.id}
                to={`/app/workspaces/${workspaceId}/surveys/${survey.id}`}
                kind="survey"
                icon={<RiSurveyLine className="size-5" aria-hidden="true" />}
                title={survey.title}
                meta={
                  <>
                    {t('surveys.questionCount', { count: survey.question_count })}
                    {survey.due_at &&
                      ` \u00b7 ${t('surveys.dueOn', { date: formatDate(survey.due_at, i18n.language) })}`}
                  </>
                }
                badges={
                  <>
                    {survey.created_by_agent && (
                      <Badge variant="default">{t('timeline.byAgent')}</Badge>
                    )}
                    <Badge variant={SURVEY_STATUS_TONE[survey.status]}>
                      {t(`surveys.status_${survey.status}`)}
                    </Badge>
                    {survey.anonymous && (
                      <Badge variant="default">{t('surveys.anonymousBadge')}</Badge>
                    )}
                    {survey.allows_public && (
                      <Badge variant="default">{t('surveys.publicBadge')}</Badge>
                    )}
                    {answer && <Badge variant={answer.tone}>{t(answer.key, answer.values)}</Badge>}
                  </>
                }
              />
            )
          })}
        </Card>
      )}
    </div>
  )
}
